// src/pages/SellerIntelligence/FixesPage.js
import { useContext, useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FixedSizeList } from 'react-window';
import AuthContext from '../../../../context/AuthContext';
import styles from './FixesPage.module.css';
import { getFixes, getFixStatus, dismissFix, deleteFix } from '../services/catalogueHealthApi';

const ROW_HEIGHT = 72;
const VIRTUALIZE_THRESHOLD = 50;

const STATUS_LABELS = {
  SUBMITTED:  { label: 'Submitted',  color: '#2766aa', bg: 'rgba(39,102,170,0.1)', border: '#78b3f3' },
  PENDING:    { label: 'Pending',    color: '#b45309', bg: '#fffbeb',              border: '#fcd34d' },
  RESOLVED:   { label: 'Resolved',   color: '#16a34a', bg: '#f0fdf4',              border: '#86efac' },
  REJECTED:   { label: 'Rejected',   color: '#de524a', bg: '#fff4f3',              border: '#ffa29d' },
  DISMISSED:  { label: 'Dismissed',  color: '#898989', bg: '#f5f5f5',              border: '#d5d5d5' },
};

const fmt = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const truncate = (str, n = 60) => str && str.length > n ? str.slice(0, n) + '…' : str || '—';

const StatusBadge = ({ status }) => {
  const cfg = STATUS_LABELS[status] || STATUS_LABELS.SUBMITTED;
  return (
    <span className={styles.statusBadge} style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}>
      {cfg.label}
    </span>
  );
};

const Spinner = () => (
  <svg className={styles.spinner} width="13" height="13" viewBox="0 0 13 13" fill="none">
    <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="2" strokeDasharray="22" strokeDashoffset="8" strokeLinecap="round"/>
  </svg>
);

const FixesPage = () => {
  const authCtx = useContext(AuthContext);
  const queryClient = useQueryClient();

  // Single unified action state — keyed by fix_id
  const [actionState, setActionState] = useState({ checking: {}, dismissing: {}, deleting: {} });
  const [checkingAll, setCheckingAll] = useState(false);
  const [checkAllProgress, setCheckAllProgress] = useState({ done: 0, total: 0 });

  const { data: fixes = [], isLoading, error } = useQuery({
    queryKey: ['catalogue-health-fixes'],
    queryFn: () => getFixes(authCtx.token),
    enabled: !!authCtx.token,
    staleTime: 30_000,
  });

  const setAction = useCallback((type, fixId, val) =>
    setActionState((prev) => ({ ...prev, [type]: { ...prev[type], [fixId]: val } })), []);

  const patchFix = useCallback((fixId, patch) =>
    queryClient.setQueryData(['catalogue-health-fixes'], (prev = []) =>
      prev.map((f) => f.fix_id === fixId ? { ...f, ...patch } : f)
    ), [queryClient]);

  const handleGetStatus = useCallback(async (fix) => {
    setAction('checking', fix.fix_id, true);
    try {
      const result = await getFixStatus(authCtx.token, fix.sku, fix.issue_code);
      patchFix(fix.fix_id, { status: result.status, last_checked_at: new Date().toISOString() });
    } catch (e) {
      console.error('get-status error:', e);
    } finally {
      setAction('checking', fix.fix_id, false);
    }
  }, [authCtx.token, patchFix, setAction]);

  const handleDismiss = useCallback(async (fix) => {
    if (!fix.fix_id) return;
    setAction('dismissing', fix.fix_id, true);
    try {
      await dismissFix(authCtx.token, fix.fix_id);
      patchFix(fix.fix_id, { status: 'DISMISSED' });
    } catch (e) {
      console.error('dismiss error:', e);
    } finally {
      setAction('dismissing', fix.fix_id, false);
    }
  }, [authCtx.token, patchFix, setAction]);

  const handleDelete = useCallback(async (fix) => {
    if (!fix.fix_id) return;
    if (!window.confirm('Permanently delete this fix record?')) return;
    setAction('deleting', fix.fix_id, true);
    try {
      await deleteFix(authCtx.token, fix.fix_id);
      queryClient.setQueryData(['catalogue-health-fixes'], (prev = []) =>
        prev.filter((f) => f.fix_id !== fix.fix_id)
      );
    } catch (e) {
      console.error('delete error:', e);
    } finally {
      setAction('deleting', fix.fix_id, false);
    }
  }, [authCtx.token, queryClient, setAction]);

  const handleGetAllStatus = useCallback(async () => {
    const checkable = fixes.filter((f) => f.status !== 'DISMISSED' && f.status !== 'RESOLVED');
    if (!checkable.length) return;

    setCheckingAll(true);
    setCheckAllProgress({ done: 0, total: checkable.length });

    const results = await Promise.allSettled(
      checkable.map((fix) =>
        getFixStatus(authCtx.token, fix.sku, fix.issue_code)
          .then((result) => ({ fix, result }))
      )
    );

    results.forEach((settled) => {
      if (settled.status === 'fulfilled') {
        const { fix, result } = settled.value;
        patchFix(fix.fix_id, { status: result.status, last_checked_at: new Date().toISOString() });
      }
    });

    setCheckAllProgress({ done: checkable.length, total: checkable.length });
    setCheckingAll(false);
  }, [fixes, authCtx.token, patchFix]);

  const tableHeader = (
    <thead>
      <tr>
        <th>Product</th><th>Attribute</th><th>Before</th><th>After</th>
        <th>Reason</th><th>Issue Code</th><th>Submission ID</th>
        <th>Applied At</th><th>Status</th><th>Actions</th>
      </tr>
    </thead>
  );

  const FixRow = useCallback(({ fix }) => (
    <tr key={fix.fix_id} style={{ opacity: fix.status === 'DISMISSED' ? 0.5 : 1 }}>
      <td>
        <div className={styles.productCell}>
          <span className={styles.productTitle}>{truncate(fix.product_title, 50)}</span>
          <span className={styles.skuTag}>{fix.sku}</span>
        </div>
      </td>
      <td><span className={styles.attrTag}>{fix.attribute || '—'}</span></td>
      <td><span className={styles.valueOld}>{truncate(fix.old_value, 80)}</span></td>
      <td><span className={styles.valueNew}>{truncate(fix.new_value, 80)}</span></td>
      <td><span className={styles.reasonText}>{truncate(fix.reason, 100)}</span></td>
      <td>{fix.issue_code ? <span className={styles.codeTag}>{fix.issue_code}</span> : '—'}</td>
      <td><span className={styles.submissionId}>{fix.submission_id || '—'}</span></td>
      <td>
        <div className={styles.dateCell}>
          <span>{fmt(fix.applied_at)}</span>
          {fix.last_checked_at && (
            <span className={styles.checkedAt}>Checked {fmt(fix.last_checked_at)}</span>
          )}
        </div>
      </td>
      <td><StatusBadge status={fix.status} /></td>
      <td>
        <div className={styles.actionCell}>
          {fix.status !== 'DISMISSED' && fix.status !== 'RESOLVED' && (
            <button type="button" className={styles.statusBtn} onClick={() => handleGetStatus(fix)} disabled={actionState.checking[fix.fix_id]}>
              {actionState.checking[fix.fix_id] ? <><Spinner />Checking…</> : 'Get Status'}
            </button>
          )}
          {fix.status !== 'DISMISSED' && fix.fix_id && (
            <button type="button" className={styles.dismissBtn} onClick={() => handleDismiss(fix)} disabled={actionState.dismissing[fix.fix_id]}>
              {actionState.dismissing[fix.fix_id] ? 'Dismissing…' : 'Dismiss'}
            </button>
          )}
          {fix.fix_id && (
            <button type="button" className={styles.deleteBtn} onClick={() => handleDelete(fix)} disabled={actionState.deleting[fix.fix_id]} title="Delete record">
              {actionState.deleting[fix.fix_id] ? '…' : (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2 3.5h10M5.5 3.5V2.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v1M6 6.5v3M8 6.5v3M3 3.5l.7 7.2a.5.5 0 0 0 .5.3h5.6a.5.5 0 0 0 .5-.3L11 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          )}
        </div>
      </td>
    </tr>
  ), [actionState, handleGetStatus, handleDismiss, handleDelete]);

  // Virtualized row renderer for react-window (used when fixes exceed threshold)
  const VirtualRow = useMemo(() => ({ index, style }) => {
    const fix = fixes[index];
    return (
      <div style={style} className={styles.virtualRow}>
        <FixRow fix={fix} />
      </div>
    );
  }, [fixes, FixRow]);

  return (
    <div className={styles.container}>
      <div className={styles.headerBlock}>
        <div className={styles.headerRow}>
          <div>
            <h1 className={styles.pageTitle}>Fix History</h1>
            <span className={styles.subTitle}>All AI-applied listing fixes and their submission status</span>
          </div>
          {fixes.length > 0 && (
            <button type="button" className={styles.getAllStatusBtn} onClick={handleGetAllStatus} disabled={checkingAll}>
              {checkingAll ? `Checking ${checkAllProgress.done}/${checkAllProgress.total}…` : 'Get All Status'}
            </button>
          )}
        </div>
      </div>

      {isLoading && <div className={styles.stateMsg}>Loading fix history…</div>}
      {error && <div className={styles.errorMsg}>{error.message}</div>}

      {!isLoading && !error && fixes.length === 0 && (
        <div className={styles.emptyBlock}>
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none"><circle cx="20" cy="20" r="20" fill="#edf6ff"/><path d="M13 20l5 5 9-10" stroke="#2766aa" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <p className={styles.emptyText}>No fixes applied yet. Use "Fix with AI" on the Catalogue Health page to apply fixes.</p>
        </div>
      )}

      {!isLoading && !error && fixes.length > 0 && (
        <div className={styles.tableWrapper}>
          {fixes.length > VIRTUALIZE_THRESHOLD ? (
            <FixedSizeList
              height={Math.min(fixes.length * ROW_HEIGHT, 600)}
              itemCount={fixes.length}
              itemSize={ROW_HEIGHT}
              width="100%"
            >
              {VirtualRow}
            </FixedSizeList>
          ) : (
            <table className={styles.table}>
              {tableHeader}
              <tbody>
                {fixes.map((fix) => <FixRow key={fix.fix_id} fix={fix} />)}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default FixesPage;
